import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * Popup, das aufgeht, sobald ein Ausloeser-Produkt in den Warenkorb gelegt wird.
 *
 * Die gesamte Zustandslogik (wie viele Ausloeser-Artikel liegen im Warenkorb,
 * ist die Belohnung freigeschaltet, liegt sie schon drin) rechnet Liquid in
 * `sections/cart-upsell-popup.liquid` aus. Diese Komponente entscheidet nur,
 * *wann* das Popup erscheint, und holt sich nach jedem Warenkorb-Zugang eine
 * frische Fassung der Section.
 *
 * Warum nicht `sectionRenderer.renderSection()`? Das morpht die komplette
 * Section inklusive `<dialog>`. Da die Antwort das `open`-Attribut nie enthaelt,
 * wuerde das Popup bei jeder Aktualisierung zuklappen – genau dann, wenn drin
 * ein T-Shirt hinzugefuegt wurde und das Schloss aufgehen soll. Deshalb wird
 * hier nur der Inhalt (`ref="body"`) getauscht, der Dialog selbst bleibt stehen.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - Der Dialog.
 * @property {HTMLElement} body - Der austauschbare Inhalt.
 *
 * @extends DialogComponent
 */
class CartUpsellPopup extends DialogComponent {
  requiredRefs = ['dialog', 'body'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** Merkt sich pro Kampagne, ob sie in dieser Session schon gezeigt/weggeklickt wurde. */
  #storageKey = 'barrio:upsell-popup:seen';

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate, {
      signal: this.#abortController.signal,
    });

    this.addEventListener('dialog:close', this.#handleClose, { signal: this.#abortController.signal });

    if (window.Shopify?.designMode) this.#watchDesignMode();
  }

  /**
   * Im Theme-Editor gibt es keinen Warenkorb-Zugang, den man mitschneiden koennte.
   * Ohne diesen Haken bekaeme der Haendler das Popup nie zu Gesicht und koennte
   * es nicht gestalten. Das Auswaehlen des Blocks in der Seitenleiste oeffnet es.
   */
  #watchDesignMode() {
    const { signal } = this.#abortController;

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const panel = event.target instanceof Element ? event.target.closest('[data-campaign-id]') : null;
        if (!(panel instanceof HTMLElement)) return;

        this.#activate(panel);
        this.showDialog();
      },
      { signal }
    );

    document.addEventListener(
      'shopify:block:deselect',
      (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('[data-campaign-id]')) return;

        this.closeDialog();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /* ----------------------------------------------------------------------- */
  /* Kampagnen aus dem DOM                                                    */
  /* ----------------------------------------------------------------------- */

  /**
   * Die Kampagnen-Panels. Bewusst bei jedem Zugriff frisch aus dem DOM gelesen:
   * nach einem Morph sind es andere Knoten mit anderen Zustandswerten.
   *
   * @returns {HTMLElement[]}
   */
  get #panels() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-campaign-id]')]);
  }

  /**
   * Sucht die Kampagne, die auf ein hinzugefuegtes Produkt passt.
   *
   * @param {string | null} productId
   * @returns {HTMLElement | undefined}
   */
  #findPanel(productId) {
    if (!productId) return undefined;

    // Die Liste kommt aus Liquid schon mit fuehrendem und abschliessendem Komma,
    // damit `,12,` nicht faelschlich in `,123,` trifft.
    return this.#panels.find((panel) => (panel.dataset.triggerIds ?? '').includes(`,${productId},`));
  }

  /**
   * Liest die Produkt-ID des ausloesenden Formulars aus dem Event.
   *
   * `CartLinesUpdateEvent` liefert in `lines[].merchandiseId` nur die Varianten-ID.
   * Die Kampagnen sind aber auf Produkte konfiguriert, und jede Varianten-ID zu
   * rendern waere bei einer Kollektion mit Groessen und Farben ein Vielfaches an
   * Markup. Das Event wird auf `<product-form-component>` ausgeloest, und das
   * traegt die Produkt-ID bereits als Attribut.
   *
   * @param {Event} event
   * @returns {string | null}
   */
  #productIdFromEvent(event) {
    const target = event.target instanceof Element ? event.target.closest('[data-product-id]') : null;

    return target?.getAttribute('data-product-id') ?? null;
  }

  /**
   * Kam der Zugang aus dem Belohnungs-Schritt des Popups?
   *
   * Bewusst ueber die Herkunft im Markup und nicht ueber die Belohnungs-
   * Kollektion: der Kunde koennte dasselbe Schmuckstueck auch von einer
   * Produktseite aus legen, und dort waere das Popup gar nicht im Spiel.
   *
   * @param {Event} event
   * @returns {boolean}
   */
  #isRewardAdd(event) {
    return Boolean(event.target instanceof Element && event.target.closest('.upsell-step--reward'));
  }

  /**
   * Oeffnet den Warenkorb-Drawer, sofern das Theme einen hat.
   *
   * Auf der Warenkorb-Seite selbst gibt es keinen – dort sieht der Kunde den
   * Warenkorb ohnehin schon, und das Schliessen des Popups genuegt.
   *
   * @returns {Promise<void>}
   */
  async #openCartDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    // Das Drawer-Modul laedt mit niedriger Prioritaet. Ohne dieses Warten
    // waere `open` beim ersten Mal noch keine Methode, sondern undefined.
    await customElements.whenDefined('theme-drawer');

    /** @type {{ open?: () => void }} */ (drawer).open?.();
  }

  /* ----------------------------------------------------------------------- */
  /* Haeufigkeit                                                              */
  /* ----------------------------------------------------------------------- */

  /** @returns {Record<string, number>} */
  #readSeen() {
    try {
      return JSON.parse(sessionStorage.getItem(this.#storageKey) ?? '{}');
    } catch {
      return {};
    }
  }

  /** @param {string} campaignId */
  #markSeen(campaignId) {
    try {
      sessionStorage.setItem(this.#storageKey, JSON.stringify({ ...this.#readSeen(), [campaignId]: Date.now() }));
    } catch {
      // Privater Modus ohne sessionStorage: dann erscheint das Popup eben jedes Mal.
    }
  }

  /**
   * Darf diese Kampagne gerade erscheinen?
   *
   * @param {HTMLElement} panel
   * @returns {boolean}
   */
  #mayOpen(panel) {
    // Im Theme-Editor wuerde eine gemerkte Anzeige das Gestalten blockieren.
    if (window.Shopify?.designMode) return true;

    const { campaignId = '', frequency = 'until_goal', rewardInCart = 'false' } = panel.dataset;

    // Liegt die Belohnung schon im Warenkorb, ist die Kampagne eingeloest. Nur
    // "always" ignoriert das – fuer Laeden, die bewusst mehrfach erinnern wollen.
    if (rewardInCart === 'true' && frequency !== 'always') return false;

    if (frequency === 'session' && this.#readSeen()[campaignId]) return false;

    return true;
  }

  /**
   * Oeffnet die Kampagne eines Produkts direkt – fuer den Bundle-Button der
   * Produktseite. Der hat das erste Shirt schon eingelegt; das zweite waehlt
   * der Kunde hier in Schritt 1.
   *
   * Der Inhalt wird *vor* dem Oeffnen neu geholt. Das Markup stammt vom
   * Seitenaufruf; ohne diesen Schritt zeigte das Popup fuer einen Moment noch
   * "0/2" und sprang dann um.
   *
   * @param {string} productId
   * @returns {Promise<boolean>} Ob das Popup uebernimmt. Sonst muss der
   *   Aufrufer selbst eine Antwort geben (z. B. den Drawer oeffnen).
   */
  async openForProduct(productId) {
    await this.#refresh();

    const panel = this.#findPanel(productId);
    if (!panel || !this.#mayOpen(panel)) return false;

    this.#activate(panel);
    this.showDialog();
    this.#markSeen(panel.dataset.campaignId ?? '');

    return true;
  }

  /* ----------------------------------------------------------------------- */
  /* Warenkorb-Ereignisse                                                     */
  /* ----------------------------------------------------------------------- */

  /**
   * Beantwortet dem Cart-Drawer synchron, ob dieses Popup den Zugang uebernimmt.
   *
   * Der Drawer darf sich dann nicht selbst oeffnen, sonst liegen zwei Dialoge
   * uebereinander. Die Antwort muss synchron fallen, weil `cart-drawer.js` seine
   * Entscheidung im selben Tick trifft – auf die Reihenfolge der Listener ist
   * kein Verlass, deshalb fragt der Drawer hier aktiv nach.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   * @returns {boolean}
   */
  willHandle(event) {
    if (event.action !== 'add') return false;

    // Aus dem offenen Popup heraus legt man T-Shirts nach. Auch diese Zugaenge
    // gehoeren dem Popup, sonst schiebt sich der Drawer davor.
    if (this.refs.dialog?.open) return true;

    // Der Drawer hat Vorrang, solange er offen ist: was dort passiert (z. B. die
    // Schmuck-Auswahl im Warenkorb), soll nicht in ein Popup umgeleitet werden.
    if (document.querySelector('#cart-drawer[open]')) return false;

    const panel = this.#findPanel(this.#productIdFromEvent(event));

    return Boolean(panel && this.#mayOpen(panel));
  }

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartLinesUpdate = (event) => {
    if (event.action !== 'add') {
      // Aendert sich der Warenkorb anderswo (z. B. Belohnung im Drawer wieder
      // entfernt), wird der Zustand in den Panels falsch. `willHandle` muss aber
      // synchron antworten und liest genau diesen Zustand – also wird er hier
      // nachgezogen, sonst bliebe das Popup nach einem Entfernen fuer immer aus.
      event.promise?.then(() => this.#refresh()).catch(() => {});
      return;
    }

    const wasOpen = Boolean(this.refs.dialog?.open);

    if (!this.willHandle(event)) return;

    // Mit der Belohnung ist die Aktion eingeloest. Danach gehoert die
    // Aufmerksamkeit dem Warenkorb – das Popup haette nichts mehr anzubieten.
    if (wasOpen && this.#isRewardAdd(event)) {
      // Sofort vermerken, dass die Belohnung eingeloest ist. Der Zustand kaeme
      // sonst erst mit dem Nachladen der Section – das dauert mehrere Sekunden,
      // und in dieser Zeit haelt `#mayOpen` die Kampagne noch fuer offen. Ein
      // schneller zweiter Zugang wuerde dann ein zweites Gratis-Stueck
      // anbieten, das der Rabatt gar nicht deckt.
      const rewardPanel =
        event.target instanceof Element ? event.target.closest('[data-campaign-id]') : null;

      if (rewardPanel instanceof HTMLElement) rewardPanel.dataset.rewardInCart = 'true';

      // Ohne Warten auf die Warenkorb-Antwort: die kostet ueber eine Sekunde,
      // und der Drawer zeigt ohnehin den Server-Stand an und aktualisiert sich
      // selbst, sobald die Antwort da ist.
      //
      // Erst schliessen, dann oeffnen: sonst liegen zwei modale Dialoge
      // uebereinander und der Drawer erbt den Fokus des Popups.
      this.closeDialog()
        .then(() => this.#openCartDrawer())
        .catch(() => {});

      // Der Zustand wird im Hintergrund nachgezogen, damit das Popup beim
      // naechsten Mal nicht mit veralteten Daten entscheidet.
      event.promise
        ?.then(({ detail }) => (detail?.didError ? undefined : this.#refresh()))
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[cart-upsell-popup] Belohnung fehlgeschlagen:', error);
        });

      return;
    }

    // Sofort oeffnen, nicht erst nach Warenkorb-Antwort und Section-Abruf:
    // die beiden Rundreisen zusammen dauerten rund anderthalb Sekunden, in
    // denen nach dem Klick sichtbar nichts passierte.
    //
    // Gezeigt wird zunaechst der Stand von vor diesem Zugang. Der Nachlauf
    // unten korrigiert ihn – und weil der Fortschrittsbalken dabei sichtbar
    // hochlaeuft, wird aus der Verzoegerung die Rueckmeldung auf den Klick.
    if (!wasOpen) {
      const panel = this.#findPanel(this.#productIdFromEvent(event));
      if (!panel) return;

      this.#activate(panel);
      this.showDialog();
      this.#markSeen(panel.dataset.campaignId ?? '');
    }

    event.promise
      ?.then(({ detail }) => {
        // Schlug der Zugang fehl, stand das Popup zu Unrecht offen.
        if (detail?.didError) {
          if (!wasOpen) this.closeDialog();
          return undefined;
        }

        return this.#refresh();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-upsell-popup] Event-Promise abgelehnt:', error);
      });
  };

  /* ----------------------------------------------------------------------- */
  /* Darstellung                                                              */
  /* ----------------------------------------------------------------------- */

  /**
   * Blendet genau eine Kampagne ein. Alle Panels bleiben im DOM, damit ein
   * spaeterer Zugang ohne erneuten Section-Abruf umschalten kann.
   *
   * @param {HTMLElement} panel
   */
  #activate(panel) {
    const id = panel.dataset.campaignId;
    this.dataset.activeCampaign = id ?? '';

    for (const candidate of this.#panels) {
      candidate.toggleAttribute('hidden', candidate.dataset.campaignId !== id);
    }
  }

  /**
   * Holt die Section neu und tauscht nur den Inhalt aus.
   *
   * @returns {Promise<void>}
   */
  async #refresh() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);

    try {
      // `no-store`, weil sich der Warenkorb bei jedem Zugang aendert und eine
      // gecachte Antwort das Schloss faelschlich geschlossen zeigen wuerde.
      const html = await fetch(url.toString(), { cache: 'no-store' }).then((response) => response.text());
      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const nextBody = fragment.querySelector('[ref="body"]');

      if (!nextBody || !this.refs.body) return;

      // Die aktive Kampagne aus der Antwort uebernehmen, sonst blendet der Morph
      // alle Panels wieder ein (im Server-Markup sind sie alle `hidden`).
      const activeId = this.dataset.activeCampaign;
      morph(this.refs.body, /** @type {HTMLElement} */ (nextBody), MORPH_OPTIONS);

      if (activeId) {
        const panel = this.#panels.find((candidate) => candidate.dataset.campaignId === activeId);
        if (panel) this.#activate(panel);
      }
    } catch (error) {
      console.warn('[cart-upsell-popup] Section konnte nicht aktualisiert werden:', error);
    }
  }

  #handleClose = () => {
    const activeId = this.dataset.activeCampaign;
    if (activeId) this.#markSeen(activeId);
  };
}

if (!customElements.get('cart-upsell-popup')) {
  customElements.define('cart-upsell-popup', CartUpsellPopup);
}
